/**
 * MetricTile — плитка-счётчик (кружок со значком, число, подпись). Общая для главной
 * («Открытые задачи / Непрочитанное / Отпускные дни») и «Состояния» (отпуск, командировка…).
 *
 * Необязательно:
 *  • `minWidth` — меньше, чтобы плитки вставали по две в ряд на телефоне («Состояние»: 2×2);
 *  • `compact` — значок над числом: в узкой плитке подпись получает всю ширину и не рвётся
 *    посреди слова («В командировк-е» на телефоне, 29.10.2026);
 *  • `onPress` + `active` — плитка-фильтр (нажать — выбрать, ещё раз — снять), выбранная
 *    обведена акцентом.
 */
import { Stack, Text, YStack } from 'tamagui'

import { useApp } from '../i18n'
import type { ToneColors } from '../theme/tones'
import { AnimatedNumber } from './AnimatedNumber'
import { Icon } from './Icon'
import { Card, pressable } from './primitives'
import { Skeleton } from './Skeleton'

/** A single dashboard metric, fed straight from a live API count. */
export interface Metric {
  key: string
  /** Ключ словаря подписи. */
  label: string
  value: number | string
  icon: string
  color: ToneColors
  /** Первая загрузка источника — вместо нуля рисуем скелетон. */
  loading?: boolean
}

export function MetricTile({
  m,
  minWidth = 210,
  compact,
  active,
  onPress,
}: {
  m: Metric
  minWidth?: number
  compact?: boolean
  active?: boolean
  onPress?: () => void
}) {
  const { l } = useApp()
  const icon = compact ? 36 : 48
  return (
    <Card
      {...(onPress
        ? { ...pressable, onPress, cursor: 'pointer', 'aria-pressed': !!active, hoverStyle: { borderColor: '$primary' } }
        : null)}
      // pressable гасит фон/рамку/поля <button> — возвращаем карточные после спреда
      backgroundColor={active ? '$primaryBg' : '$background'}
      borderWidth={1}
      borderColor={active ? '$primary' : '$borderSecondary'}
      flex={1}
      minWidth={minWidth}
      padding={compact ? 12 : 16}
      flexDirection={compact ? 'column' : 'row'}
      alignItems={compact ? 'flex-start' : 'center'}
      gap={compact ? 8 : 14}
    >
      <Stack width={icon} height={icon} borderRadius={999} backgroundColor={m.color.soft} alignItems="center" justifyContent="center">
        <Icon name={m.icon} size={compact ? 16 : 20} color={m.color.text} />
      </Stack>
      <YStack flex={compact ? undefined : 1} minWidth={0}>
        {m.loading ? (
          <Skeleton width={44} height={26} borderRadius={6} marginVertical={1} />
        ) : typeof m.value === 'number' ? (
          // live metric — counts up to the value instead of popping in
          <AnimatedNumber value={m.value} fontSize={26} fontWeight="700" color="$color" lineHeight={28} />
        ) : (
          <Text fontSize={26} fontWeight="700" color="$color" lineHeight={28}>
            {m.value}
          </Text>
        )}
        <Text fontSize={12} fontWeight="600" color="$muted" marginTop={3}>
          {l(m.label)}
        </Text>
      </YStack>
    </Card>
  )
}
